import { type UseQueryResult, useQuery } from '@tanstack/react-query'
import { ZodTypeAny, z } from 'zod'

import { apiClient } from '~/api'
import { ShopeeLogisticsProvider } from '~/common/schemas/logistics-shopee'
import { DeliveryOption } from '~/common/schemas/logistics-tiktok'
import type { ApiError } from '~/utils/error'

type ChannelWithLogistics = 'shopee' | 'tiktok'

function makeLogisicsSchema<T extends ZodTypeAny>(schema: T) {
    return z.record(z.coerce.number(), schema.array())
}

const ShopeeLogisticsProviderServer = ShopeeLogisticsProvider.omit({ enabled: true })
type ShopeeLogisticsProviderServer = z.infer<typeof ShopeeLogisticsProviderServer>

type ChannelSchema<T extends ChannelWithLogistics> = T extends 'tiktok'
    ? DeliveryOption[]
    : T extends 'shopee'
      ? ShopeeLogisticsProviderServer[]
      : never

function parseLogistics<T extends ChannelWithLogistics>(channel: T, response: unknown) {
    switch (channel) {
        case 'tiktok':
            return makeLogisicsSchema(DeliveryOption).parse(response)
        case 'shopee':
            return makeLogisicsSchema(ShopeeLogisticsProviderServer).parse(response)
        default:
            throw new Error(`Unsupported channel: ${channel}`)
    }
}

export function useChannelLogisticsQuery<T extends ChannelWithLogistics>({
    channel,
    shopId,
}: { channel: T; shopId: number }): UseQueryResult<ChannelSchema<T>, ApiError> {
    return useQuery({
        retry: 3,
        queryKey: ['logistics', { shopId, channel }],
        queryFn: async () => {
            const response = await apiClient
                .get(`channels/${channel}/logistics/`, { searchParams: { shop_id: shopId } })
                .json()

            const result = parseLogistics(channel, response)

            return result[shopId]
        },
        enabled: !!channel,
        staleTime: Infinity,
    })
}
